/**
 * Database Integration Service
 *
 * This service provides a unified interface for database operations,
 * handling both Firebase and localStorage with appropriate fallbacks.
 */
import { v4 as uuidv4 } from 'uuid';

import { mockAtms } from '../data/mockAtms';
import {
  Atm,
  AtmListItem,
  AtmShareSettings,
  CreateAtmDto,
  SharedAtm,
  SharedAtmTransaction,
} from '../types/atm';
import { SharedWheel, SharedWheelSpin, ShareSettings, Wheel } from '../types/wheel';
import { auth } from './firebase';
import * as FirebaseAtmService from './firebaseAtmService';
import * as FirebaseWheelService from './firebaseWheelService';
import * as LocalStorageService from './localStorage';

/**
 * Check if user is authenticated
 */
const isAuthenticated = (): boolean => {
  return !!auth.currentUser;
};

/**
 * Create a new wheel
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const createWheel = async (wheelData: Partial<Wheel>): Promise<string> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      return await FirebaseWheelService.createWheel(wheelData);
    } else {
      // Use localStorage if not authenticated
      const wheel = await LocalStorageService.saveWheel(wheelData);
      return wheel.id;
    }
  } catch (error) {
    console.error('Error creating wheel:', error);
    // Fallback to localStorage on error
    const wheel = await LocalStorageService.saveWheel(wheelData);
    return wheel.id;
  }
};

/**
 * Get a wheel by ID
 * Tries Firebase first, falls back to localStorage
 */
export const getWheel = async (
  wheelId: string,
  isParentOfSharedWheel = false
): Promise<Wheel | null> => {
  try {
    // Allow access to parent wheels of shared wheels without authentication
    if (isAuthenticated() || isParentOfSharedWheel) {
      const firebaseWheel = await FirebaseWheelService.getWheel(wheelId);
      if (firebaseWheel) {
        return firebaseWheel;
      }
    }

    // Try localStorage as fallback
    return LocalStorageService.getStoredWheelById(wheelId);
  } catch (error) {
    console.error('Error getting wheel:', error);
    return LocalStorageService.getStoredWheelById(wheelId);
  }
};

/**
 * Update a wheel
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const updateWheel = async (wheelId: string, wheelData: Partial<Wheel>): Promise<boolean> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      return await FirebaseWheelService.updateWheel(wheelId, wheelData);
    } else {
      // Use localStorage if not authenticated
      const wheel = LocalStorageService.getStoredWheelById(wheelId);
      if (!wheel) {
        return false;
      }

      const updatedWheel = {
        ...wheel,
        ...wheelData,
      };

      LocalStorageService.updateStoredWheel(updatedWheel);
      return true;
    }
  } catch (error) {
    console.error('Error updating wheel:', error);

    // Fallback to localStorage on error
    try {
      const wheel = LocalStorageService.getStoredWheelById(wheelId);
      if (!wheel) {
        return false;
      }

      const updatedWheel = {
        ...wheel,
        ...wheelData,
      };

      LocalStorageService.updateStoredWheel(updatedWheel);
      return true;
    } catch (fallbackError) {
      console.error('Error in localStorage fallback:', fallbackError);
      return false;
    }
  }
};

/**
 * Delete a wheel
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const deleteWheel = async (wheelId: string): Promise<boolean> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      return await FirebaseWheelService.deleteWheel(wheelId);
    } else {
      // Use localStorage if not authenticated
      return LocalStorageService.deleteStoredWheel(wheelId);
    }
  } catch (error) {
    console.error('Error deleting wheel:', error);

    // Fallback to localStorage on error
    return LocalStorageService.deleteStoredWheel(wheelId);
  }
};

/**
 * List wheels
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const listWheels = async (
  filters: Record<string, any> = {},
  pageSize = 20
): Promise<Wheel[]> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      return await FirebaseWheelService.listWheels(filters, pageSize);
    } else {
      // Use localStorage if not authenticated
      const wheels = LocalStorageService.getStoredWheels();

      // Apply filters
      const filteredWheels = wheels.filter(wheel => {
        return Object.entries(filters).every(([key, value]) => {
          // Using type assertion instead of @ts-ignore
          return (wheel as Record<string, any>)[key] === value;
        });
      });

      return filteredWheels.slice(0, pageSize);
    }
  } catch (error) {
    console.error('Error listing wheels:', error);

    // Fallback to localStorage on error
    const wheels = LocalStorageService.getStoredWheels();

    // Apply filters
    const filteredWheels = wheels.filter(wheel => {
      return Object.entries(filters).every(([key, value]) => {
        // Using type assertion instead of @ts-ignore
        return (wheel as Record<string, any>)[key] === value;
      });
    });

    return filteredWheels.slice(0, pageSize);
  }
};

/**
 * Get user's wheels
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const getUserWheels = async (userId: string, pageSize = 20): Promise<Wheel[]> => {
  if (isAuthenticated()) {
    return FirebaseWheelService.getUserWheels(userId, pageSize);
  } else {
    // For localStorage, we don't have user association
    // Return all wheels as a fallback
    return LocalStorageService.getStoredWheels().slice(0, pageSize);
  }
};

/**
 * Get public wheels
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const getPublicWheels = async (pageSize = 20): Promise<Wheel[]> => {
  if (isAuthenticated()) {
    return FirebaseWheelService.getPublicWheels(pageSize);
  } else {
    // For localStorage, we don't have public/private distinction
    // Return all wheels as a fallback
    return LocalStorageService.getStoredWheels().slice(0, pageSize);
  }
};

/**
 * Increment spin count for a wheel
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const incrementSpinCount = async (wheelId: string): Promise<boolean> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      return await FirebaseWheelService.incrementSpinCount(wheelId);
    } else {
      // Use localStorage if not authenticated
      const updatedWheel = LocalStorageService.incrementSpinCount(wheelId);
      return !!updatedWheel;
    }
  } catch (error) {
    console.error('Error incrementing spin count:', error);

    // Fallback to localStorage on error
    const updatedWheel = LocalStorageService.incrementSpinCount(wheelId);
    return !!updatedWheel;
  }
};

/**
 * Migrate wheels from localStorage to Firebase
 * Only works when authenticated
 */
export const migrateLocalWheelsToFirebase = async (): Promise<string[]> => {
  if (!isAuthenticated()) {
    throw new Error('User must be authenticated to migrate wheels');
  }

  const localWheels = LocalStorageService.getStoredWheels();
  const migratedWheelIds: string[] = [];

  for (const wheel of localWheels) {
    try {
      const wheelId = await FirebaseWheelService.migrateWheelToFirestore(wheel);
      migratedWheelIds.push(wheelId);

      // Optionally delete from localStorage after successful migration
      // LocalStorageService.deleteStoredWheel(wheel.id);
    } catch (error) {
      console.error(`Error migrating wheel ${wheel.id}:`, error);
    }
  }

  return migratedWheelIds;
};

/**
 * Create a wheel share
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const createWheelShare = async (
  wheelId: string,
  shareSettings: ShareSettings
): Promise<string> => {
  if (!isAuthenticated()) {
    throw new Error('User must be authenticated to create a wheel share');
  }

  try {
    return await FirebaseWheelService.createWheelShare(wheelId, shareSettings);
  } catch (error) {
    console.error('Error creating wheel share:', error);
    throw new Error('Failed to create wheel share');
  }
};

/**
 * Get a shared wheel by ID
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const getSharedWheel = async (shareId: string): Promise<SharedWheel | null> => {
  try {
    // Shared wheels should be accessible without authentication
    return await FirebaseWheelService.getSharedWheel(shareId);
  } catch (error) {
    console.error('Error getting shared wheel:', error);
    throw new Error('Failed to get shared wheel');
  }
};

/**
 * Get all shares for a wheel
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const getWheelShares = async (wheelId: string): Promise<SharedWheel[]> => {
  if (!isAuthenticated()) {
    throw new Error('User must be authenticated to get wheel shares');
  }

  try {
    return await FirebaseWheelService.getWheelShares(wheelId);
  } catch (error) {
    console.error('Error getting wheel shares:', error);
    throw new Error('Failed to get wheel shares');
  }
};

/**
 * Update a wheel share
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const updateWheelShare = async (
  wheelId: string,
  shareId: string,
  settings: Partial<ShareSettings>
): Promise<boolean> => {
  if (!isAuthenticated()) {
    throw new Error('User must be authenticated to update a wheel share');
  }

  try {
    return await FirebaseWheelService.updateWheelShare(wheelId, shareId, settings);
  } catch (error) {
    console.error('Error updating wheel share:', error);
    throw new Error('Failed to update wheel share');
  }
};

/**
 * Delete a wheel share
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const deleteWheelShare = async (wheelId: string, shareId: string): Promise<boolean> => {
  if (!isAuthenticated()) {
    throw new Error('User must be authenticated to delete a wheel share');
  }

  try {
    return await FirebaseWheelService.deleteWheelShare(wheelId, shareId);
  } catch (error) {
    console.error('Error deleting wheel share:', error);
    throw new Error('Failed to delete wheel share');
  }
};

/**
 * Record a spin on a shared wheel
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const spinSharedWheel = async (
  shareId: string,
  spinData: SharedWheelSpin
): Promise<boolean> => {
  try {
    // Shared wheels should be spinnable without authentication
    return await FirebaseWheelService.recordSharedWheelSpin(shareId, spinData);
  } catch (error) {
    console.error('Error spinning shared wheel:', error);
    throw new Error('Failed to spin shared wheel');
  }
};

/**
 * Get all spins for a shared wheel
 * Uses Firebase if authenticated, localStorage is not supported for sharing
 */
export const getSharedWheelSpins = async (shareId: string): Promise<SharedWheelSpin[]> => {
  try {
    // Shared wheel spins should be accessible without authentication
    return await FirebaseWheelService.getSharedWheelSpins(shareId);
  } catch (error) {
    console.error('Error getting shared wheel spins:', error);
    throw new Error('Failed to get shared wheel spins');
  }
};

// ============== ATM OPERATIONS ==============

// Local storage keys for ATMs
const LOCAL_ATMS_KEY = 'spoil_engine_atms';
const LOCAL_SHARED_ATMS_KEY = 'spoil_engine_shared_atms';

/**
 * Get ATMs from local storage
 */
const getLocalAtms = (): Atm[] => {
  try {
    console.log('📚 getLocalAtms: Fetching ATMs from localStorage...');
    const storedAtms = LocalStorageService.getStoredAtms();
    console.log('📚 getLocalAtms: Found', storedAtms.length, 'ATMs in localStorage');
    console.log('📚 getLocalAtms: Raw storedAtms data:', storedAtms);

    if (storedAtms.length > 0) {
      console.log(
        '📚 getLocalAtms: ATM IDs found:',
        storedAtms.map(a => a.id)
      );
      console.log('📚 getLocalAtms: Returning stored ATMs');
      return storedAtms;
    }

    // Fallback to mock data if no ATMs are stored
    console.log('📚 getLocalAtms: No ATMs found in localStorage, returning mock data');
    console.log(
      '📚 getLocalAtms: Mock ATM IDs:',
      mockAtms.map(a => a.id)
    );
    return [...mockAtms];
  } catch (error) {
    console.error('Error getting ATMs from localStorage:', error);
    console.log('📚 getLocalAtms: Error occurred, returning mock data as fallback');
    return [...mockAtms];
  }
};

/**
 * Save ATMs to local storage
 */
const saveLocalAtms = (atms: Atm[]): void => {
  localStorage.setItem(LOCAL_ATMS_KEY, JSON.stringify(atms));
};

/**
 * Get shared ATMs from local storage
 */
const getLocalSharedAtms = (): Record<string, SharedAtm> => {
  const storedSharedAtms = localStorage.getItem(LOCAL_SHARED_ATMS_KEY);
  if (storedSharedAtms) {
    try {
      return JSON.parse(storedSharedAtms);
    } catch (error) {
      console.error('Error parsing stored shared ATMs:', error);
      return {};
    }
  }
  return {};
};

/**
 * Save shared ATMs to local storage
 */
const saveLocalSharedAtms = (sharedAtms: Record<string, SharedAtm>): void => {
  localStorage.setItem(LOCAL_SHARED_ATMS_KEY, JSON.stringify(sharedAtms));
};

/**
 * Create a new ATM
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const createAtm = async (atmData: CreateAtmDto): Promise<string> => {
  try {
    console.log('🔥 DatabaseIntegration: Starting ATM creation');
    console.log('👤 DatabaseIntegration: User authenticated?', isAuthenticated());
    console.log('👤 DatabaseIntegration: Current user:', auth.currentUser?.uid || 'none');

    if (isAuthenticated() && auth.currentUser) {
      // Use Firebase if authenticated - UNLIMITED ATMs
      console.log('☁️ DatabaseIntegration: Using Firebase (unlimited ATMs)');
      return await FirebaseAtmService.createAtm(atmData, auth.currentUser.uid);
    } else {
      // Use localStorage - LIMITED to 1 ATM for anonymous users
      console.log('💾 DatabaseIntegration: Using localStorage (anonymous user)');
      const existingAtms = getLocalAtms();

      // FREEMIUM LIMIT: Only allow 1 ATM for anonymous users
      if (existingAtms.length >= 1) {
        console.log('🚫 DatabaseIntegration: Anonymous user ATM limit reached (1/1)');
        throw new Error('Anonymous users can only create 1 ATM. Sign up for unlimited ATMs!');
      }

      const atmId = uuidv4();
      console.log('🆔 DatabaseIntegration: Generated ATM ID:', atmId, '(1/1 anonymous limit)');

      // Generate IDs for options if not provided
      const options = atmData.options.map(option => ({
        ...option,
        id: option.id || uuidv4(),
      }));

      const newAtm: Atm = {
        ...atmData,
        id: atmId,
        options,
        createdBy: 'anonymous',
        createdByName: 'Anonymous User',
        createdAt: new Date(),
        updatedAt: new Date(),
        transactionCount: 0,
        transactions: [],
        shareSettings: [],
      };

      existingAtms.push(newAtm);
      saveLocalAtms(existingAtms);
      console.log('✅ DatabaseIntegration: ATM saved to localStorage, returning ID:', atmId);

      return atmId;
    }
  } catch (error) {
    console.error('Error creating ATM:', error);
    // Fallback to localStorage
    const atms = getLocalAtms();
    const atmId = uuidv4();

    const options = atmData.options.map(option => ({
      ...option,
      id: option.id || uuidv4(),
    }));

    const newAtm: Atm = {
      ...atmData,
      id: atmId,
      options,
      createdBy: 'anonymous',
      createdByName: 'Anonymous User',
      createdAt: new Date(),
      updatedAt: new Date(),
      transactionCount: 0,
      transactions: [],
      shareSettings: [],
    };

    atms.push(newAtm);
    saveLocalAtms(atms);

    return atmId;
  }
};

/**
 * Get an ATM by ID
 * Tries Firebase first, falls back to localStorage
 */
export const getAtm = async (atmId: string, includeShareSettings = false): Promise<Atm | null> => {
  try {
    console.log('🔍 DatabaseIntegration: getAtm called for ID:', atmId);
    console.log('👤 DatabaseIntegration: User authenticated?', isAuthenticated());

    if (isAuthenticated()) {
      // User is authenticated - try Firebase first
      console.log('☁️ DatabaseIntegration: User authenticated, trying Firebase first...');
      const firebaseAtm = await FirebaseAtmService.getAtm(atmId, includeShareSettings);
      if (firebaseAtm) {
        return firebaseAtm;
      }
    }

    // Try localStorage (either user not authenticated, or Firebase didn't find it)
    console.log('💾 DatabaseIntegration: Trying localStorage...');
    const atms = getLocalAtms();
    console.log('💾 DatabaseIntegration: Looking for ATM ID:', atmId);
    console.log(
      '💾 DatabaseIntegration: Available ATM IDs:',
      atms.map(a => a.id)
    );
    const atm = atms.find(a => a.id === atmId);
    if (atm) {
      console.log('✅ DatabaseIntegration: Found ATM in localStorage');
      return atm;
    } else {
      console.log('❌ DatabaseIntegration: ATM not found in localStorage');
    }

    // If user is not authenticated and not in localStorage, still try Firebase as fallback
    if (!isAuthenticated()) {
      console.log(
        '☁️ DatabaseIntegration: Not authenticated but trying Firebase as final fallback...'
      );
      const firebaseAtm = await FirebaseAtmService.getAtm(atmId, includeShareSettings);
      if (firebaseAtm) {
        return firebaseAtm;
      }
    }

    console.log('❌ DatabaseIntegration: ATM not found anywhere');
    return null;
  } catch (error) {
    console.error('Error getting ATM:', error);
    const atms = getLocalAtms();
    const atm = atms.find(a => a.id === atmId);
    return atm || null;
  }
};

/**
 * Update an ATM
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const updateAtm = async (atmId: string, atmData: Partial<Atm>): Promise<boolean> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      await FirebaseAtmService.updateAtm(atmId, atmData);
      return true;
    } else {
      // Use localStorage if not authenticated
      const atms = getLocalAtms();
      const atmIndex = atms.findIndex(a => a.id === atmId);

      if (atmIndex !== -1) {
        const updatedAtm = {
          ...atms[atmIndex],
          ...atmData,
          updatedAt: new Date(),
        };

        atms[atmIndex] = updatedAtm;
        saveLocalAtms(atms);
        return true;
      }
      return false;
    }
  } catch (error) {
    console.error('Error updating ATM:', error);

    // Fallback to localStorage
    const atms = getLocalAtms();
    const atmIndex = atms.findIndex(a => a.id === atmId);

    if (atmIndex !== -1) {
      const updatedAtm = {
        ...atms[atmIndex],
        ...atmData,
        updatedAt: new Date(),
      };

      atms[atmIndex] = updatedAtm;
      saveLocalAtms(atms);
      return true;
    }
    return false;
  }
};

/**
 * Delete an ATM
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const deleteAtm = async (atmId: string): Promise<boolean> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      await FirebaseAtmService.deleteAtm(atmId);
      return true;
    } else {
      // Use localStorage if not authenticated
      const atms = getLocalAtms();
      const filteredAtms = atms.filter(a => a.id !== atmId);
      saveLocalAtms(filteredAtms);

      // Also clean up any shared ATMs for this ATM
      const sharedAtms = getLocalSharedAtms();
      const updatedSharedAtms: Record<string, SharedAtm> = {};

      for (const [shareId, sharedAtm] of Object.entries(sharedAtms)) {
        if (sharedAtm.atmId !== atmId) {
          updatedSharedAtms[shareId] = sharedAtm;
        }
      }

      saveLocalSharedAtms(updatedSharedAtms);
      return true;
    }
  } catch (error) {
    console.error('Error deleting ATM:', error);

    // Fallback to localStorage
    const atms = getLocalAtms();
    const filteredAtms = atms.filter(a => a.id !== atmId);
    saveLocalAtms(filteredAtms);
    return true;
  }
};

/**
 * Get user's ATMs
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const getUserAtms = async (pageSize = 100): Promise<AtmListItem[]> => {
  try {
    let firebaseAtms: AtmListItem[] = [];
    let localAtms: AtmListItem[] = [];

    // Get Firebase ATMs if authenticated
    if (isAuthenticated() && auth.currentUser) {
      console.log('📊 DatabaseIntegration: Getting Firebase ATMs for user:', auth.currentUser.uid);
      try {
        firebaseAtms = await FirebaseAtmService.getUserAtms(auth.currentUser.uid, pageSize);
        console.log(`☁️ DatabaseIntegration: Found ${firebaseAtms.length} Firebase ATMs`);
      } catch (error) {
        console.error('Error getting Firebase ATMs:', error);
      }
    }

    // Always get local ATMs as well (they might have ATMs created while offline)
    const atms = getLocalAtms();
    localAtms = atms.map(atm => ({
      id: atm.id,
      name: atm.name,
      description: atm.description,
      createdBy: atm.createdBy,
      createdByName: atm.createdByName,
      createdAt: atm.createdAt,
      theme: atm.theme,
      isPublic: atm.isPublic,
      transactionCount: atm.transactionCount,
    }));
    console.log(`💾 DatabaseIntegration: Found ${localAtms.length} local ATMs`);

    // Merge Firebase and local ATMs, avoiding duplicates by ID
    const combinedAtms = [...firebaseAtms];
    for (const localAtm of localAtms) {
      if (!firebaseAtms.some(fbAtm => fbAtm.id === localAtm.id)) {
        combinedAtms.push(localAtm);
      }
    }

    console.log(
      `🔗 DatabaseIntegration: Returning ${combinedAtms.length} total ATMs (${
        firebaseAtms.length
      } Firebase + ${localAtms.length} local, ${
        firebaseAtms.length + localAtms.length - combinedAtms.length
      } duplicates removed)`
    );
    return combinedAtms;
  } catch (error) {
    console.error('Error getting user ATMs:', error);

    // Fallback to localStorage only
    const atms = getLocalAtms();
    return atms.map(atm => ({
      id: atm.id,
      name: atm.name,
      description: atm.description,
      createdBy: atm.createdBy,
      createdByName: atm.createdByName,
      createdAt: atm.createdAt,
      theme: atm.theme,
      isPublic: atm.isPublic,
      transactionCount: atm.transactionCount,
    }));
  }
};

/**
 * Get public ATMs
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const getPublicAtms = async (pageSize = 20): Promise<AtmListItem[]> => {
  try {
    if (isAuthenticated()) {
      // Use Firebase if authenticated
      return await FirebaseAtmService.getPublicAtms(pageSize);
    } else {
      // Use localStorage if not authenticated
      const atms = getLocalAtms();
      return atms
        .filter(atm => atm.isPublic)
        .map(atm => ({
          id: atm.id,
          name: atm.name,
          description: atm.description,
          createdBy: atm.createdBy,
          createdByName: atm.createdByName,
          createdAt: atm.createdAt,
          theme: atm.theme,
          isPublic: atm.isPublic,
          transactionCount: atm.transactionCount,
        }))
        .slice(0, pageSize);
    }
  } catch (error) {
    console.error('Error getting public ATMs:', error);

    // Fallback to localStorage
    const atms = getLocalAtms();
    return atms
      .filter(atm => atm.isPublic)
      .map(atm => ({
        id: atm.id,
        name: atm.name,
        description: atm.description,
        createdBy: atm.createdBy,
        createdByName: atm.createdByName,
        createdAt: atm.createdAt,
        theme: atm.theme,
        isPublic: atm.isPublic,
        transactionCount: atm.transactionCount,
      }))
      .slice(0, pageSize);
  }
};

/**
 * Share an ATM
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const shareAtm = async (atmId: string, settings: AtmShareSettings): Promise<string> => {
  try {
    if (isAuthenticated()) {
      // When authenticated, first try Firebase sharing
      try {
        console.log('🔗 DatabaseIntegration: Trying to share ATM via Firebase:', atmId);
        return await FirebaseAtmService.shareAtm(atmId, settings);
      } catch (error) {
        console.error(
          '❌ DatabaseIntegration: Firebase share failed, ATM might be local-only:',
          error
        );

        // If Firebase fails, the ATM might only exist locally
        // We need to first migrate the ATM to Firebase, then share it
        const localAtms = getLocalAtms();
        const localAtm = localAtms.find(atm => atm.id === atmId);

        if (localAtm && auth.currentUser) {
          console.log('📤 DatabaseIntegration: Found local ATM, migrating to Firebase first...');

          // Create the ATM in Firebase
          const createData: CreateAtmDto = {
            name: localAtm.name,
            description: localAtm.description,
            options: localAtm.options,
            theme: localAtm.theme,
            isPublic: localAtm.isPublic,
            atmSettings: localAtm.atmSettings,
          };

          // Create in Firebase (this will generate a new ID)
          const firebaseAtmId = await FirebaseAtmService.createAtm(
            createData,
            auth.currentUser.uid
          );
          console.log(
            '✅ DatabaseIntegration: ATM migrated to Firebase with new ID:',
            firebaseAtmId
          );

          // Now share the Firebase version
          return await FirebaseAtmService.shareAtm(firebaseAtmId, settings);
        }

        // If we can't find the local ATM or migrate it, throw the original error
        throw error;
      }
    } else {
      // Use localStorage if not authenticated
      const atms = getLocalAtms();
      const atmIndex = atms.findIndex(a => a.id === atmId);

      if (atmIndex === -1) {
        throw new Error('ATM not found');
      }

      // Update the ATM with this share setting
      const atm = atms[atmIndex];
      const updatedShareSettings = [...(atm.shareSettings || []), settings];

      atm.shareSettings = updatedShareSettings;
      atm.updatedAt = new Date();

      atms[atmIndex] = atm;
      saveLocalAtms(atms);

      // Create the shared ATM - generate a shareId if not provided
      const shareId = Date.now().toString();
      const sharedAtm: SharedAtm = {
        shareId,
        atmId: atmId,
        atmName: atm.name,
        description: atm.description,
        ownerId: 'anonymous', // TODO: get from auth
        ownerName: 'Anonymous',
        options: atm.options, // Include full ATM options
        theme: atm.theme, // Include theme
        atmSettings: atm.atmSettings, // Include settings
        shareSettings: settings,
        transactionCount: 0,
        transactions: [],
        isActive: true,
        createdAt: new Date(),
        updatedAt: new Date(),
      };

      // Save to local storage
      const sharedAtms = getLocalSharedAtms();
      sharedAtms[shareId] = sharedAtm;
      saveLocalSharedAtms(sharedAtms);

      return shareId;
    }
  } catch (error) {
    console.error('Error sharing ATM:', error);
    throw new Error('Failed to share ATM');
  }
};

/**
 * Get a shared ATM
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const getSharedAtm = async (shareId: string): Promise<SharedAtm | null> => {
  try {
    console.log('🔍 getSharedAtm: Looking for shared ATM with ID:', shareId);

    // Always try Firebase first, then fall back to localStorage
    try {
      console.log('☁️ getSharedAtm: Checking Firebase...');
      const firebaseResult = await FirebaseAtmService.getSharedAtm(shareId);
      if (firebaseResult) {
        console.log('✅ getSharedAtm: Found shared ATM in Firebase');
        return firebaseResult;
      }
      console.log('❌ getSharedAtm: Shared ATM not found in Firebase, checking localStorage...');
    } catch (firebaseError) {
      console.log(
        '⚠️ getSharedAtm: Firebase check failed, checking localStorage...',
        firebaseError
      );
    }

    // Fall back to localStorage
    console.log('💾 getSharedAtm: Checking localStorage...');
    const sharedAtms = getLocalSharedAtms();
    const localResult = sharedAtms[shareId] || null;

    if (localResult) {
      console.log('✅ getSharedAtm: Found shared ATM in localStorage');
      return localResult;
    }

    console.log('❌ getSharedAtm: Shared ATM not found in localStorage either');
    return null;
  } catch (error) {
    console.error('💥 getSharedAtm: Error getting shared ATM:', error);
    // Last resort fallback to localStorage
    const sharedAtms = getLocalSharedAtms();
    return sharedAtms[shareId] || null;
  }
};

/**
 * Process a transaction on a shared ATM
 * Uses Firebase if authenticated, localStorage otherwise
 */
export const processSharedAtmTransaction = async (
  shareId: string,
  transaction: SharedAtmTransaction
): Promise<void> => {
  try {
    console.log('💰 processSharedAtmTransaction: Processing transaction for share ID:', shareId);

    // Always try Firebase first, then fall back to localStorage
    let processedInFirebase = false;

    try {
      console.log('☁️ processSharedAtmTransaction: Attempting to process in Firebase...');
      await FirebaseAtmService.processSharedAtmTransaction(shareId, transaction);
      processedInFirebase = true;
      console.log('✅ processSharedAtmTransaction: Transaction processed in Firebase');
      return;
    } catch (firebaseError) {
      console.log(
        '⚠️ processSharedAtmTransaction: Firebase processing failed, trying localStorage...',
        firebaseError
      );
    }

    // Fall back to localStorage if Firebase failed
    if (!processedInFirebase) {
      console.log('💾 processSharedAtmTransaction: Processing in localStorage...');
      const sharedAtms = getLocalSharedAtms();
      const sharedAtm = sharedAtms[shareId];

      if (!sharedAtm) {
        console.log('❌ processSharedAtmTransaction: Shared ATM not found in localStorage');
        throw new Error('Shared ATM not found');
      }

      // Check if transactions are allowed
      if (!sharedAtm.isActive) {
        throw new Error('This ATM is no longer active');
      }

      if (
        sharedAtm.shareSettings.maxTransactions !== null &&
        sharedAtm.transactionCount >= (sharedAtm.shareSettings.maxTransactions || 0)
      ) {
        throw new Error('Maximum transactions reached for this ATM');
      }

      if (
        sharedAtm.shareSettings.expiresAt instanceof Date &&
        sharedAtm.shareSettings.expiresAt < new Date()
      ) {
        throw new Error('This shared ATM has expired');
      }

      // Update the shared ATM
      sharedAtm.transactions.push(transaction);
      sharedAtm.transactionCount += 1;
      sharedAtm.updatedAt = new Date();

      sharedAtms[shareId] = sharedAtm;
      saveLocalSharedAtms(sharedAtms);

      // Update the parent ATM transaction count
      const atms = getLocalAtms();
      const atmIndex = atms.findIndex(a => a.id === sharedAtm.atmId);

      if (atmIndex !== -1) {
        atms[atmIndex].transactionCount += 1;
        saveLocalAtms(atms);
      }

      console.log('✅ processSharedAtmTransaction: Transaction processed in localStorage');
    }
  } catch (error) {
    console.error(
      '💥 processSharedAtmTransaction: Error processing shared ATM transaction:',
      error
    );
    throw error;
  }
};

// ============== NEW ATM SHARING FUNCTIONS FOR MODAL ==============

/**
 * Create a new shared ATM with modern sharing pattern
 * Used by the enhanced ATM share modal
 */
export const createSharedAtm = async (sharedAtm: SharedAtm): Promise<void> => {
  if (!isAuthenticated()) {
    throw new Error('User must be authenticated to create shared ATMs');
  }

  try {
    console.log('📤 DatabaseIntegration: Creating shared ATM with ID:', sharedAtm.shareId);

    // Use Firebase for storing shared ATMs (matching getSharedAtm behavior)
    await FirebaseAtmService.createSharedAtm(sharedAtm);

    console.log('✅ DatabaseIntegration: Shared ATM created successfully');
  } catch (error) {
    console.error('❌ DatabaseIntegration: Error creating shared ATM:', error);

    // Fallback to localStorage (for development/offline use)
    console.log('📱 DatabaseIntegration: Falling back to localStorage');
    const sharedAtms = getLocalSharedAtms();
    sharedAtms[sharedAtm.shareId] = sharedAtm;
    saveLocalSharedAtms(sharedAtms);
    console.log('💾 DatabaseIntegration: Shared ATM saved to localStorage');
  }
};

/**
 * Get all shares for an ATM
 * Used by the enhanced ATM share modal
 */
export const getAtmShares = async (atmId: string): Promise<SharedAtm[]> => {
  try {
    console.log('📚 DatabaseIntegration: Getting shares for ATM:', atmId);
    console.log('📚 DatabaseIntegration: User authenticated?', isAuthenticated());

    // Try Firebase first (works even if not authenticated, Firebase rules will handle permissions)
    if (isAuthenticated()) {
      try {
        const firebaseShares = await FirebaseAtmService.getAtmShares(atmId);
        console.log('✅ DatabaseIntegration: Retrieved', firebaseShares.length, 'shares from Firebase');
        return firebaseShares;
      } catch (error) {
        console.error('❌ DatabaseIntegration: Error getting ATM shares from Firebase:', error);
      }
    } else {
      console.log('⚠️ DatabaseIntegration: Not authenticated, skipping Firebase query');
    }

    // Fallback to localStorage
    console.log('💾 DatabaseIntegration: Checking localStorage');
    const sharedAtms = getLocalSharedAtms();
    const localShares = Object.values(sharedAtms).filter(share => share.atmId === atmId);
    console.log('💾 DatabaseIntegration: Found', localShares.length, 'shares in localStorage');
    return localShares;
  } catch (error) {
    console.error('❌ DatabaseIntegration: Error getting ATM shares:', error);
    // Return empty array instead of throwing to avoid breaking the UI
    return [];
  }
};

/**
 * Get all transactions for a shared ATM
 * Used by the enhanced ATM share modal
 */
export const getSharedAtmTransactions = async (
  shareId: string
): Promise<SharedAtmTransaction[]> => {
  try {
    console.log('📚 DatabaseIntegration: Getting transactions for share:', shareId);

    // Try Firebase first
    try {
      const sharedAtm = await FirebaseAtmService.getSharedAtm(shareId);
      if (sharedAtm && sharedAtm.transactions) {
        console.log(
          '✅ DatabaseIntegration: Retrieved',
          sharedAtm.transactions.length,
          'transactions from Firebase'
        );
        return sharedAtm.transactions;
      }
    } catch (firebaseError) {
      console.error('❌ DatabaseIntegration: Error getting transactions from Firebase:', firebaseError);
    }

    // Fallback to localStorage
    console.log('💾 DatabaseIntegration: Checking localStorage');
    const sharedAtms = getLocalSharedAtms();
    const sharedAtm = sharedAtms[shareId];
    const localTransactions = sharedAtm?.transactions || [];
    console.log('💾 DatabaseIntegration: Found', localTransactions.length, 'transactions in localStorage');
    return localTransactions;
  } catch (error) {
    console.error('❌ DatabaseIntegration: Error getting shared ATM transactions:', error);
    // Return empty array instead of throwing to avoid breaking the UI
    return [];
  }
};

/**
 * Update the status of a shared ATM (activate/deactivate)
 * Used by the enhanced ATM share modal
 */
export const updateSharedAtmStatus = async (shareId: string, isActive: boolean): Promise<void> => {
  try {
    console.log('📚 DatabaseIntegration: Updating shared ATM status:', shareId, 'to', isActive);

    // Try Firebase first if authenticated
    if (isAuthenticated()) {
      await FirebaseAtmService.updateSharedAtmStatus(shareId, isActive);
      console.log('✅ DatabaseIntegration: Shared ATM status updated in Firebase');
      return;
    }

    // Fallback to localStorage
    console.log('💾 DatabaseIntegration: Using localStorage (not authenticated)');
    const sharedAtms = getLocalSharedAtms();
    const sharedAtm = sharedAtms[shareId];

    if (!sharedAtm) {
      throw new Error('Shared ATM not found');
    }

    sharedAtm.isActive = isActive;
    sharedAtm.updatedAt = new Date();
    sharedAtms[shareId] = sharedAtm;
    saveLocalSharedAtms(sharedAtms);
  } catch (error) {
    console.error('❌ DatabaseIntegration: Error updating shared ATM status:', error);
    throw error;
  }
};

/**
 * Delete a shared ATM
 * Used by the enhanced ATM share modal
 */
export const deleteSharedAtm = async (shareId: string): Promise<void> => {
  try {
    console.log('📚 DatabaseIntegration: Deleting shared ATM:', shareId);

    // Try Firebase first if authenticated
    if (isAuthenticated()) {
      await FirebaseAtmService.deleteSharedAtm(shareId);
      console.log('✅ DatabaseIntegration: Shared ATM deleted from Firebase');
      return;
    }

    // Fallback to localStorage
    console.log('💾 DatabaseIntegration: Using localStorage (not authenticated)');
    const sharedAtms = getLocalSharedAtms();
    delete sharedAtms[shareId];
    saveLocalSharedAtms(sharedAtms);
  } catch (error) {
    console.error('❌ DatabaseIntegration: Error deleting shared ATM:', error);
    throw error;
  }
};
